import { create } from 'zustand';
import { RoleType, Session, User } from '../types/RoleType';
import { persist } from 'zustand/middleware';
import { AuthAPI } from '../services/api/auth';
import { usersApi } from '../services/api/user';
import { ApiResponse } from '../types/common';
import { setSentryUser, clearSentryUser } from '../lib/sentry';
import { identifyUser, resetMixpanel } from '../lib/mixpanel';
import { setAppInsightsUser, clearAppInsightsUser, flushAppInsights } from '../lib/appInsights';
import { setCsrfToken, clearCsrfToken } from '../utils/csrf';
import type { PendingProfileSelection } from '../types/auth';
import type { PhoneVerificationChallenge } from '../types/RoleType';

export interface PendingPasswordSetup {
  challengeToken: string;
  expiresAt: string;
}

export interface PendingPasswordEntry {
  username: string;
  roleType: string;
}

export interface AuthState {
  session: Session | null;
  error: string | null;
  isAuthenticated: boolean;
  pendingProfileSelection: PendingProfileSelection | null;
  pendingPhoneVerification: PhoneVerificationChallenge | null;
  pendingPasswordSetup: PendingPasswordSetup | null;
  pendingPasswordEntry: PendingPasswordEntry | null;
  login: (username: string, password: string, roleType: RoleType) => Promise<void>;
  logout: () => void;
  logoutAll: () => void;
  clearError: () => void;
  hasRole: (role: string) => boolean;
  hasRoleType: (roleType: RoleType) => boolean;
  hasPermission: (permission: string) => boolean;
  updateSession: (session: Session) => void;
  updateUser: (user: User) => Promise<ApiResponse<boolean>>;
  checkSession: () => void;
  updateLastActivity: () => void;
  lastActivity: number;
  selectProfile: (studentId: string) => Promise<void>;
  reopenProfilePicker: () => Promise<void>;
  hasHydrated: boolean;
  setHasHydrated: (hydrated: boolean) => void;
}

// Cross-tab session coordination. The backend rotates the refresh token on every
// refresh, but each tab's session lives in its own sessionStorage — without this,
// a refresh in tab A invalidates tab B's stored refresh token, and tab B's next
// refresh attempt gets rejected as invalid, wrongly logging everyone out.
function broadcastAuth(message: 'logout' | { type: 'session-updated'; session: Session }) {
  try {
    const bc = new BroadcastChannel('auth');
    bc.postMessage(message);
    bc.close();
  } catch {
    // BroadcastChannel not supported in all environments
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => (
      {
        isAuthenticated: false,
        error: null,
        session: null,
        pendingProfileSelection: null,
        pendingPhoneVerification: null,
        pendingPasswordSetup: null,
        pendingPasswordEntry: null,
        lastActivity: Date.now(),
        hasHydrated: false,
        setHasHydrated: (hydrated: boolean) => set({ hasHydrated: hydrated }),
        login: async (username: string, password: string, roleType: RoleType) => {
          try {
            const result = await AuthAPI.login(username, password, roleType);

            if (result.kind === 'requiresPassword') {
              set({
                pendingPasswordEntry: { username: result.username, roleType },
                pendingPasswordSetup: null,
                pendingPhoneVerification: null,
                pendingProfileSelection: null,
                isAuthenticated: false,
                session: null,
              });
              return;
            }

            if (result.kind === 'passwordSetup') {
              set({
                pendingPasswordSetup: { challengeToken: result.challengeToken, expiresAt: result.expiresAt },
                pendingPasswordEntry: null,
                pendingProfileSelection: null,
                pendingPhoneVerification: null,
                session: null,
                isAuthenticated: false,
                error: null,
              });
              return;
            }

            if (result.kind === 'phoneVerification') {
              set({
                pendingPhoneVerification: result.phoneVerification,
                pendingPasswordEntry: null,
                pendingProfileSelection: null,
                pendingPasswordSetup: null,
                session: null,
                isAuthenticated: false,
                error: null,
              });
              return;
            }

            if (result.kind === 'profileSelection') {
              // Multi-student: store picker data, don't create a session yet
              set({
                pendingProfileSelection: result.pendingProfileSelection,
                pendingPasswordEntry: null,
                pendingPhoneVerification: null,
                session: null,
                isAuthenticated: false,
                error: null,
              });
              return;
            }

            // Single student / admin / employee: normal session
            const session: Session = {
              accessToken: result.accessToken,
              refreshToken: result.refreshToken,
              expiresAt: result.expiresAt,
              refreshTokenExpiresAt: result.refreshTokenExpiresAt,
              user: result.user,
              forcePasswordReset: result.forcePasswordReset,
              forcePinChange: result.forcePinChange,
              emailConfirmed: result.emailConfirmed,
            };
            set({ session, isAuthenticated: true, error: null, pendingProfileSelection: null, pendingPhoneVerification: null });
            setSentryUser(result.user);
            setAppInsightsUser(String(result.user.id), result.user.instituteName);
            identifyUser(result.user);
          }
          catch (error) {
            set({ error: error instanceof Error ? error.message : 'An error occurred', isAuthenticated: false });
            throw error;
          }
        },
        updateUser: async (user: User) => {
          const response = await usersApi.updateUser(user);
          if (response.succeeded) {
            const { session } = get();
            if (session) {
              set({
                session: {
                  ...session, user: {
                    ...session.user,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    email: user.email,
                    profileImageUrl: user.profileImageUrl,
                  }
                }
              });
            }
          }
          return response;
        },
        updateSession: (session: Session) => {
          set({ session, isAuthenticated: true });
          broadcastAuth({ type: 'session-updated', session });
        },
        logout: async () => {
          try {
            const { session } = get();
            if (session?.refreshToken) {
              await AuthAPI.logout(session.user.id, session.refreshToken);
            }
          } finally {
            set({ session: null, isAuthenticated: false, error: null, pendingProfileSelection: null, pendingPhoneVerification: null, pendingPasswordSetup: null, pendingPasswordEntry: null });
            // Clear monitoring user contexts and flush buffered telemetry
            clearSentryUser();
            clearAppInsightsUser();
            flushAppInsights();
            // Reset Mixpanel
            resetMixpanel();
            // Clear CSRF token
            clearCsrfToken();
            // Notify other tabs to logout
            broadcastAuth('logout');
          }
        },
        logoutAll: async () => {
          try {
            const { session } = get();
            if (session?.user.id) {
              await AuthAPI.logoutAll(session.user.id);
            }
          } finally {
            set({ session: null, isAuthenticated: false, error: null, pendingProfileSelection: null, pendingPhoneVerification: null, pendingPasswordSetup: null, pendingPasswordEntry: null });
            // Clear monitoring user contexts and flush buffered telemetry
            clearSentryUser();
            clearAppInsightsUser();
            flushAppInsights();
            // Reset Mixpanel
            resetMixpanel();
            // Clear CSRF token
            clearCsrfToken();
            // Notify other tabs to logout
            broadcastAuth('logout');
          }
        },
        clearError: () => {
          set({ error: null });
        },

        hasPermission: (permission: string) => {
          const { session } = get();
          if (!session?.user) return false;
          return session!.user.permissions.includes(permission);
        },
        hasRoleType: (roleType: RoleType) => {
          const { session } = get();
          if (!session?.user) return false;
          return session!.user.roleType.toLocaleLowerCase() === roleType.toLocaleLowerCase();
        },
        hasRole: (role: string) => {
          const { session } = get();
          if (!session?.user) return false;
          return session!.user.roles.includes(role);
        },
        selectProfile: async (studentId: string) => {
          const { pendingProfileSelection } = get();
          if (!pendingProfileSelection) throw new Error('No pending profile selection');
          const authApi = AuthAPI as unknown as {
            selectProfile: (token: string, studentId: string) => Promise<{ user: User } & import('../types/RoleType').TokenResponse>;
          };
          const response = await authApi.selectProfile(pendingProfileSelection.token, studentId);
          const session: Session = {
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            expiresAt: response.expiresAt,
            refreshTokenExpiresAt: response.refreshTokenExpiresAt,
            user: response.user,
            forcePasswordReset: response.forcePasswordReset,
            forcePinChange: response.forcePinChange,
            emailConfirmed: response.emailConfirmed,
          };
          set({ session, isAuthenticated: true, pendingProfileSelection: null, pendingPhoneVerification: null, pendingPasswordSetup: null, error: null });
          setSentryUser(response.user);
          setAppInsightsUser(String(response.user.id), response.user.instituteName);
          identifyUser(response.user);
        },
        reopenProfilePicker: async () => {
          const authApi = AuthAPI as unknown as {
            switchProfileToken: () => Promise<import('../types/auth').PendingProfileSelection>;
          };
          const data = await authApi.switchProfileToken();
          // Mark as unauthenticated so the SELECT_PROFILE route guard allows the picker to render.
          // The session is kept so it can be restored if the user picks a profile or navigates back.
          set({ pendingProfileSelection: data, isAuthenticated: false });
        },
        updateLastActivity: () => {
          set({ lastActivity: Date.now() });
        },
        checkSession: () => {
          const { session, logout } = get();
          const now = Date.now();
          if (!session) return;

          // Check refresh token expiration
          if (now >= session.refreshTokenExpiresAt) {
            logout();
            return;
          }
        }
      }
    ),
    {
      name: 'auth-session-storage',
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
      partialize: (state) => ({
        session: state.session,
        isAuthenticated: state.isAuthenticated,
        lastActivity: state.lastActivity,
        pendingProfileSelection: state.pendingProfileSelection,
        pendingPhoneVerification: state.pendingPhoneVerification,
        pendingPasswordSetup: state.pendingPasswordSetup,
        pendingPasswordEntry: state.pendingPasswordEntry,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// Cross-tab: adopt the rotated session another tab just refreshed, instead of
// later presenting our stale (now-invalid) refresh token to the backend.
try {
  const authChannel = new BroadcastChannel('auth');
  authChannel.onmessage = (event) => {
    if (event.data && typeof event.data === 'object' && event.data.type === 'session-updated') {
      useAuthStore.setState({ session: event.data.session, isAuthenticated: true });
    }
  };
} catch {
  // BroadcastChannel not supported in all environments
}